import { styleNoyau } from '@/components/noyaux/noyaux-affichage';
import { type ChoixResultat, ONGLET_ORIGINE } from '@/lib/params-recherche';
import { cn } from '@/lib/utils';
import { Button, Icon, Tooltip } from '@pms-digital/design-system';
import { NOYAUX } from '@rosetta/shared';
import { useLinkProps } from '@tanstack/react-router';
import type { MouseEventHandler } from 'react';

interface CarteResultatProps {
  /** Identifier of the translation, for the link to its analysis. */
  traductionId: string;
  /** The noyau of the card, or `origine` for the text as it was entered (#38). */
  noyau: ChoixResultat;
  texte: string;
  onCopier: () => void;
  onTelecharger: () => void;
}

/** Colour of the code inside the 48 px dot: white on the dark noyaux, black on the light ones. */
const ENCRE_CODE = {
  'bleu-nuit': 'text-white',
  'bleu-ciel': 'text-black',
  vert: 'text-black',
  jaune: 'text-black',
  rouge: 'text-white',
  rose: 'text-white',
} as const;

/** The names of the three icon buttons of the header: accessible name and bubble, the same words. */
const LIBELLE_MODIFICATIONS = 'voir les modifications';
const LIBELLE_COPIER = 'copier';
const LIBELLE_TELECHARGER = 'télécharger';

function majuscule(mot: string): string {
  return `${mot.charAt(0).toUpperCase()}${mot.slice(1)}`;
}

/**
 * The result card of one noyau (or, with `noyau="origine"`, of the original text, #38): white, thin `gray-200` frame, 8 px corners and a 4 px
 * net in the colour of the noyau on top. Header (dot, "traduit vers", long name) with, on the
 * right of the same line, three buttons that show an icon alone (#37): "voir les modifications"
 * (always shown), "copier" and "télécharger". Each has its accessible name and a bubble that says
 * it (`Tooltip` of the design system); they drop under the name when the card is narrow, never a horizontal scroll.
 * Then the translated text, read-only and in full; no foot. The DOM order is the reading and tab
 * order: the name, "voir les modifications", "copier", "télécharger", the text. The quality
 * control is never mentioned here (#20): it lives in the analysis.
 *
 * The original text (#38) is drawn in the same card: same header, same text, read-only. Its name is
 * "Texte d'origine" (Bold then Light, like the long name of a noyau), with no dot of a noyau and
 * no icon and no "traduit vers" line, and a neutral net on top. Its header keeps the height of the
 * header of a noyau (`min-h-[53.4px]`: the dot is 48 px, the two lines of text 53.4 px) with the name
 * on the same bottom line, so nothing jumps when the tab changes. Only "copier" and "télécharger" stay:
 * "voir les modifications" opens the analysis of a translation, which a text that was not translated
 * has not.
 */
export function CarteResultat({
  traductionId,
  noyau,
  texte,
  onCopier,
  onTelecharger,
}: CarteResultatProps) {
  const original = noyau === ONGLET_ORIGINE;
  const detail = original ? undefined : NOYAUX[noyau];
  const style = original ? undefined : styleNoyau(noyau);
  // The link keeps the router's href and click (no reload, modifier keys), and goes through the plain
  // link mode of the button: the tooltip trigger puts its handlers on it, which `renderLink` would drop. The original text has no analysis: the link is not shown.
  const modifications = useLinkProps({
    to: '/resultat/$id/analyse',
    params: { id: traductionId },
    search: { ...(original ? {} : { noyau }), onglet: 'avant-apres' },
  });

  return (
    <article
      aria-label={original ? "texte d'origine" : 'texte traduit'}
      className={cn(
        'box-border w-full overflow-hidden rounded-lg border border-gray-200 border-t-4 bg-white',
        style === undefined ? 'border-t-gray-400' : style.filet,
      )}
    >
      <header className="flex flex-wrap items-end gap-x-4 gap-y-3 px-8 pt-6 pb-4">
        <div
          className={cn(
            'flex min-w-0 gap-4',
            original ? 'min-h-[53.4px] items-end' : 'items-center',
          )}
        >
          {detail === undefined || style === undefined ? null : (
            <span
              className={cn(
                'flex size-12 shrink-0 items-center justify-center rounded-full font-bold text-small tracking-[1px]',
                style.pastille,
                ENCRE_CODE[detail.couleur],
              )}
            >
              {noyau}
            </span>
          )}
          <div className="flex min-w-0 flex-col gap-1">
            {original ? null : (
              <span className="text-caption text-gray-600 leading-[1.4]">traduit vers</span>
            )}
            <span className="break-words text-h3 leading-[1.3]">
              {detail === undefined ? (
                <>
                  <span className="font-bold">Texte</span>{' '}
                  <span className="font-light">d'origine</span>
                </>
              ) : (
                <>
                  <span className="font-bold">{majuscule(detail.famille)}</span>{' '}
                  <span className="font-light">{detail.variante}</span>
                </>
              )}
            </span>
          </div>
        </div>
        <div className="-mb-2 ml-auto flex flex-wrap items-center gap-2">
          {original ? null : (
            <Tooltip content={LIBELLE_MODIFICATIONS}>
              <Button
                variant="ghost"
                size="icon"
                href={modifications.href ?? ''}
                {...(modifications.onClick === undefined
                  ? {}
                  : { onClick: modifications.onClick as MouseEventHandler<HTMLAnchorElement> })}
                aria-label={LIBELLE_MODIFICATIONS}
              >
                <Icon name="find_in_page" size={24} />
              </Button>
            </Tooltip>
          )}
          <Tooltip content={LIBELLE_COPIER}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={LIBELLE_COPIER}
              onClick={onCopier}
            >
              <Icon name="content_copy" size={24} />
            </Button>
          </Tooltip>
          <Tooltip content={LIBELLE_TELECHARGER}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={LIBELLE_TELECHARGER}
              onClick={onTelecharger}
            >
              <Icon name="download" size={24} />
            </Button>
          </Tooltip>
        </div>
      </header>
      <div className="whitespace-pre-line break-words px-8 pt-2 pb-8 font-light text-body leading-[1.6]">
        {texte}
      </div>
    </article>
  );
}
